import React, { useEffect, useRef, useState } from 'react';
import { Modal, notification, Tag, Tooltip } from 'antd';

import { Search, Button, DataGrid } from '@hydrodigital/design-system';

import { CloseOutlined, DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import DevicesService from '../../domain/Devices/DevicesService';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { iotEventsService } from '../../events/iot-events.subject';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import { useSearchParams } from 'react-router-dom';
import { BulkDevice } from '@/domain/Devices/Iot';
import useDebounce from '@/hooks/useDebounce';

const devicesService = new DevicesService();

export default function BulkDevicesView() {
	const [bulkList, setBulkList] = useState<any[]>([]);
	const [paginationState, setPaginationState] = useState({
		pageIndex: 0,
		pageSize: 10,
	});
	const [totalRows, setTotalRows] = useState(0);
	const [query, setQuery] = useState<string>('');
	const [messageError, setMessageError] = useState('');
	const [showErrorInfoModal, setShowErrorInfoModal] = useState(false);
	const [selectedIot, setSelectedIot] = useState<BulkDevice | null>(null);
	const [showIotDetails, setShowIotDetails] = useState(false);
	const subscriptionIot$ = iotEventsService.getSubject();
	const [showConfirmDelete, setShowConfirmDelete] = useState(false);
	const selectedDevice = useRef<any>(null);
	const navigate = useNavigate();

	const debouncedQuery = useDebounce(query, 500);

	const columns: ColumnsType<BulkDevice> = [
		{
			title: 'File Name',
			dataIndex: 'name',
			key: 'name',
			width: 200,
		},
		{
			title: 'Serial number',
			key: 'serialNumber',
			width: 200,
			render: (_, record) => (
				<div className="flex column">
					{record.status === 'COMPLETED' ? (
						<span
							style={{
								color: '#22A2CA',
								textDecoration: 'underline',
								cursor: 'pointer',
							}}
							onClick={() => {
								setSelectedIot(record);
								setShowIotDetails(true);
							}}
						>
							View devices
						</span>
					) : (
						<span>--</span>
					)}
				</div>
			),
		},
		{
			title: 'Upload by',
			key: 'uploadFor',
			width: 200,
			render: (_, record) => (
				<div className="flex column">
					<span
						style={{
							color: '#232B36',
							fontSize: '14px',
							fontWeight: '500',
						}}
					>
						{record.createdBy}
					</span>
					<span
						style={{
							color: '#74788D',
						}}
					>
						{dayjs(record.createdAt).format('YYYY/MM/DD [at] hh:mm a')}
					</span>
				</div>
			),
		},
		{
			title: 'Status',
			key: 'status',
			width: 200,
			render: (_, record) => (
				<span>
					<Tag
						color={
							record.status == 'COMPLETED' ? 'green' : record.status == 'FAILED' ? 'red' : 'orange'
						}
						style={{ borderRadius: '20px' }}
					>
						{record.status}
					</Tag>
				</span>
			),
		},
		{
			title: '',
			key: 'actions',
			width: 100,
			render: (_, record) => (
				<div className="flex">
					{record.status === 'FAILED' && (
						<WarningOutlined
							className="mr-1 cursor-pointer"
							style={{ fontSize: '1.5rem', color: 'red' }}
							onClick={() => {
								setSelectedIot(record);
								setMessageError('Error');
								setShowErrorInfoModal(true);
							}}
							rev={undefined}
						/>
					)}
					{record.status !== 'IN PROGRESS' && (
						<Tooltip title="Delete">
							<DeleteOutlined
								className="mr-1"
								style={{ fontSize: '1.5rem' }}
								onClick={async () => {
									selectedDevice.current = record;
									setShowConfirmDelete(true);
								}}
								rev={undefined}
							/>
						</Tooltip>
					)}
				</div>
			),
		},
	];
	const columnsDetail = [
		{
			header: 'Device',
			accessorKey: 'device',
			width: 300,
		},
		{
			header: 'Sensor',
			accessorKey: 'sensor',
			width: 300,
		},
		{
			header: 'Longitude',
			accessorKey: 'longitude',
			width: 300,
		},
		{
			header: 'Latitude',
			accessorKey: 'latitude',
			width: 300,
		},
	];

	const getDevices = async () => {
		const oui = JSON.parse(localStorage.getItem('security') || '{}').organizationId;
		const response = await devicesService.getIotByOrganization(
			oui,
			paginationState.pageIndex + 1,
			paginationState.pageSize,
			debouncedQuery || null,
		);
		console.log('🚀 ~ file: index.tsx:126 ~ getDevices ~ response:', response);
		setBulkList(response.data);
		setTotalRows(response.totalRecords);
	};

	const handleDeleteIot = async (iotId: string | number) => {
		setShowConfirmDelete(false);
		const response = await devicesService.deleteIot(iotId);
		if (response) {
			await getDevices();
			notification.success({
				message: 'Successful',
				description: 'Device deleted successfully',
			});
		}
	};

	const gridColumns = [
		{
			header: 'File Name',
			accessorKey: 'name',
			cell: ({ cell }: any) => <span className="table-data-gis">{cell.getValue()}</span>,
		},
		{
			header: 'Serial number',
			id: 'serialNumber',
			cell: ({ row }: any) => {
				const record = row.original;
				return (
					<div className="flex column">
						{record.status === 'COMPLETED' ? (
							<span
								style={{
									color: '#22A2CA',
									textDecoration: 'underline',
									cursor: 'pointer',
								}}
								onClick={() => {
									setSelectedIot(record);
									setShowIotDetails(true);
								}}
							>
								View devices
							</span>
						) : (
							<span>--</span>
						)}
					</div>
				);
			},
		},
		{
			header: 'Upload by',
			id: 'uploadFor',
			cell: ({ row }: any) => {
				const record = row.original;
				return (
					<div className="flex column">
						<span
							style={{
								color: '#232B36',
								fontSize: '14px',
								fontWeight: '500',
							}}
						>
							{record.createdBy}
						</span>
						<span style={{ color: '#74788D' }}>
							{dayjs(record.createdAt).format('YYYY/MM/DD [at] hh:mm a')}
						</span>
					</div>
				);
			},
		},
		{
			header: 'Status',
			accessorKey: 'status',
			cell: ({ row }: any) => {
				const record = row.original;
				const color =
					record.status === 'COMPLETED' ? 'green' : record.status === 'FAILED' ? 'red' : 'orange';
				return (
					<span>
						<Tag color={color} style={{ borderRadius: '20px' }}>
							{record.status}
						</Tag>
					</span>
				);
			},
		},
		{
			header: '',
			id: 'actions',
			cell: ({ row }: any) => {
				const record = row.original;
				return (
					<div className="flex">
						{record.status === 'FAILED' && (
							<WarningOutlined
								className="mr-1 cursor-pointer"
								style={{ fontSize: '1.5rem', color: 'red' }}
								onClick={() => {
									setSelectedIot(record);
									setMessageError('Error');
									setShowErrorInfoModal(true);
								}}
								rev={undefined}
							/>
						)}
						{record.status !== 'IN PROGRESS' && (
							<Tooltip title="Delete">
								<DeleteOutlined
									className="mr-1"
									style={{ fontSize: '1.5rem' }}
									onClick={async () => {
										selectedDevice.current = record;
										setShowConfirmDelete(true);
									}}
									rev={undefined}
								/>
							</Tooltip>
						)}
					</div>
				);
			},
		},
	];

	useEffect(() => {
		subscriptionIot$.subscribe((event) => {
			if (event.type == 'iotUploaded') {
				console.log(event);
				setBulkList((prev) =>
					prev.map((item) =>
						item.id === event.data.id ? { ...item, status: event.data.status } : item,
					),
				);
			}
		});
	}, []);

	useEffect(() => {
		getDevices();
	}, [paginationState.pageIndex, paginationState.pageSize, debouncedQuery]);

	useEffect(() => {
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, [debouncedQuery]);

	return (
		<div className="flex flex-col" style={{ height: 'calc(100vh - 220px)' }}>
			<span className="text-title-page mb-4" style={{ flexShrink: 0 }}>Bulk Device</span>
			{bulkList.length > 0 ? (
				<>
					<div className="flex justify-between mb-3" style={{ flexShrink: 0 }}>
						<div style={{ width: 200, minWidth: 200 }}>
							<Search
								placeholder="Search"
								size="small"
								value={query}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
									setQuery(e.target.value);
								}}
								showClearButton={true}
								onClear={() => {
									setQuery('');
								}}
								style={{ width: '100%' }}
							/>
						</div>

						<Button
							variant="primary"
							size="small"
							label="Upload IOT Files"
							iconName="plus-circle"
							iconColor="white"
							onClick={() => {
								navigate(`/bulk/upload`);
							}}
						/>
					</div>

					<DataGrid
						data={bulkList}
						columns={gridColumns}
						stickyHeader={true}
						maxHeight="calc(100vh - 380px)"
						pagination={{
							enabled: true,
							manualPagination: true,
							rowCount: totalRows,
							defaultPageSize: 10,
							pageSizeOptions: [10, 20, 50],
						}}
						paginationState={paginationState}
						onPaginationChange={setPaginationState}
					/>
				</>
			) : (
				<div
					className="flex column items-center justify-center"
					style={{ height: 'calc(100vh - 200px)' }}
				>
					<span style={{ fontSize: '16px' }} className="text-center">
						Whoops looks like you have nothing here. <br />
						Start by clicking here
					</span>
					<Button
						variant="primary"
						size="small"
						label="Upload IOT Files"
						iconName="plus-circle"
						iconColor="white"
						onClick={() => {
							navigate(`/bulk/upload`);
						}}
					/>
				</div>
			)}
			<Modal open={showErrorInfoModal} footer={[]} closable={false} style={{ maxWidth: '300px' }}>
				<div className="flex column items-center">
					<span
						className="mb-2"
						style={{ color: '#F46A6A', fontSize: '1.2rem', fontWeight: 'bold' }}
					>
						Error
					</span>
					<div
						className="flex items-center justify-center mb-3"
						style={{
							width: '150px',
							height: '56px',
							backgroundColor: '#FFD3D380',
							borderRadius: '5px',
						}}
					>
						<span
							className=""
							style={{
								color: '#F46A6A',
								fontSize: '1rem',
								fontWeight: '600',
							}}
						>
							{messageError}
						</span>
					</div>

					<div className="separator mb-2" />
					<div className="flex justify-center">
						<Button
							label="Try again"
							style={{
								width: '85px',
								height: '40px',
								backgroundColor: '#F46A6A',
								color: 'white',
							}}
							onClick={async () => {
								if (selectedIot) {
									const response = await devicesService.postCheckedIot(selectedIot.iotUploadId);
									if (response.success === 1) {
										navigate(`/bulk/upload`);
									}
								}
							}}
						/>
					</div>
				</div>
			</Modal>
			<Modal open={showIotDetails} footer={[]} closable={false} width={800}>
				<div className="flex column">
					<div className="flex justify-between">
						<span
							className="mb-2"
							style={{
								color: '#023E65',
								fontSize: '1.2rem',
								fontWeight: 'bold',
							}}
						>
							{`${selectedIot?.name} DEVICES`}
						</span>
						<CloseOutlined
							className="mb-auto"
							style={{ fontSize: '20px', cursor: 'pointer' }}
							onClick={() => {
								setShowIotDetails(false);
							}}
							rev={undefined}
						/>
					</div>
					<div className="separator mb-2" />
					<DataGrid
						columns={columnsDetail}
						data={selectedIot?.serialNumbers.split('\n').map((dev) => {
							const props = dev.split(',');
							return {
								device: props[0],
								sensor: props[1],
								longitude: props[2],
								latitude: props[3],
							};
						}) || []}
						maxHeight="calc(100vh - 300px)"
						pagination={{
							enabled: false,
						}}
					/>
				</div>
			</Modal>
			<ConfirmationModal
				title="Delete Device?"
				message="Are you sure to delete it?"
				showModal={showConfirmDelete}
				onAccept={() => {
					if (selectedDevice) {
						handleDeleteIot(selectedDevice.current.iotUploadId);
					}
				}}
				oncancel={() => {
					setShowConfirmDelete(false);
				}}
			/>
		</div>
	);
}
